import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import moment from "moment-timezone";
import produce from "immer";

// redux
import { useDispatch, useSelector } from "react-redux";
import { sessionCheck } from "../../../actions/user";
import { collectionApi } from "../../../api/CollectionApi";
import { eventApi } from "../../../api/EventApi";
import { ideaApi } from "../../../api/IdeaApi";

// child components
import { DatePicker } from "antd";
import { LabeledContainer } from "src/components/layout";
import { Input, Button, DialogModalWindow, NameDialog } from "src/components/shared";

import QuestionEditor from "./QuestionEditor";
import ParticipationSetting from "./ParticipationSetting";
import {
  defaultQuestionType,
  QUESTION_TYPES,
  reorder,
} from "../../../util/utils";
import DateTime from "../../../models/DateTime";
import { NavigationUtils } from "../../../util/NavigationUtils";
import LoadingAndPulse from "../../../components/shared/LoadingAndPulse";

import { TrackingAnalytics } from "../../../analytics/TrackingAnalytics";

import { BrowserChecker } from "../../../BrowserChecker";

// style
import "./Create.scss";

const defaultQuestion = (product) => {
  const key = Math.random().toString(36).substring(7);
  const defaultType = defaultQuestionType(product);
  return {
    key: key,
    title: "Question text",
    question_type: defaultType.value,
    options: defaultType.options,  // commented that don't work
    //options: QUESTION_TYPES.options,  // added by akmal..not worked
    voting_mode: "like-dislike",
    randomize_choice_order: true,
    enable_comments: true,
    hasFocus: true,
  };
};

const templateQuestion = (question, question_type) => {
  return {
    key: Math.random().toString(36).substring(7),
    title: question,
    question_type: question_type,
    options: QUESTION_TYPES[question_type].options,
    voting_mode: "like-dislike",
    randomize_choice_order: true,
    enable_comments: true,
    hasFocus: true,
  };
}

// A custom hook that builds on useLocation to parse
// the query string for you.
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const Create = () => {
  // Edit route has slug param
  const { productType, slug } = useParams();

  const history = useHistory();

  const query = useQuery();

  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const [currentSlug, setCurrentSlug] = useState();
  const [collectionID, setCollectionID] = useState();
  const [title, setTitle] = useState("Untitled");
  const [startOn, setStartOn] = useState(moment());
  const [endOn, setEndOn] = useState(moment().add(3, "days"));
  const [questions, setQuestions] = useState([]);
  const [accessSetting, setAccessSetting] = useState("anyone_as_guest");
  const [anonymitySetting, setAnonymitySetting] = useState("anonymous");
  const [isAnonymitySettingEditable, setAnonymitySettingEditable] =
    useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isNameDialogOpen, setNameDialogOpen] = useState(false);

  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // dispatch user from redux store
    dispatch(sessionCheck());

    const fetchCollection = async (slug) => {
      try {
        const event = await eventApi.get(slug);
        // console.log("fetchEvent", event);

        const collection = await collectionApi.getCollection(
          event.collection.id
        );
        // console.log("fetchCollection", collection);

        setCurrentSlug(event.slug);
        setCollectionID(event.collection.id);
        setTitle(collection.name || 'Untitled');
        setStartOn(moment.unix(event.schedule.ideas.start_time));
        setEndOn(moment.unix(event.schedule.ideas.end_time));
        setAccessSetting(event.visibility_setting);
        setAnonymitySetting(event.anonymous ? 'anonymous' : 'named');

        const copyParams = JSON.parse(query.get('copy'));

        // upon copy these parameters can be overriden
        if (copyParams) {
          // console.log('copyParams', copyParams);
          setCollectionID(null);
          setStartOn(moment.unix(copyParams.startTime));
          setEndOn(moment.unix(copyParams.endTime));
        }

        const qs = [];
        for (let event of collection.events) {
          // console.log("Event", event);
          const question = {
            id: event.id,
            key: event.slug,
            title: event.name,
            question_type: event.question_type,
            not_editable: event.not_editable,
            media: event.launch_media,
          };
          // if copying objects, remove id
          if (copyParams) {
            question.id = null;
          }
          if (
            event.question_type === 'multiple' ||
            event.question_type === 'multiple_rating' ||
            event.question_type === 'stack_rank'
          ) {
            const choices = await ideaApi.getIdeas(event.id);
            // console.log('Choices', choices);
            question.options = choices.map((c) => {
              return { id: c.id, name: c.name, media: c.media };
            });
            question.options_editable = true;
            question.randomize_choice_order = event.randomize_choice_order;
            question.enable_comments = event.polls_crowdsourcing;
          } else if (
            event.question_type === 'binary' ||
            event.question_type === 'scale'
          ) {
            question.options = QUESTION_TYPES[question.question_type].options;
            question.options_editable = false;
            question.enable_comments = event.polls_crowdsourcing;
          } else if (event.question_type === 'default') {
            question.voting_mode = event.voting_mode;
          }

          if (event.not_editable?.includes('anonymous')) {
            setAnonymitySettingEditable(false);
          }

          qs.push(question);
        }
        // console.log("Questions", qs);
        setQuestions(qs);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchCollection(slug);
    } else {
      setQuestions([defaultQuestion(productType)]);
      setLoading(false);
    }

    if (user.user && (!user.user.first_name || user.user.first_name == "" || !user.user.last_name || user.user.last_name == "")) {
      setNameDialogOpen(true);
    }

    // eslint-disable-next-line
  }, [dispatch, slug, currentSlug, user.user?.id]);

  // On Handlers

  const onTitleChange = (e) => {
    setTitle(e.currentTarget.value);
  };

  const validateDate = (start, end) => {
    if (!start || !end) return false;
    if (!start.isBefore(end)) {
      setErrors({
        duration: "The start date must be before the end date.",
      });
      return false;
    } else {
      setErrors({
        duration: null,
      });
      return true;
    }
  };

  const onStartOnChange = (value) => {
    setStartOn(value);
    validateDate(value, endOn);
  };

  const onEndOnChange = (value) => {
    setEndOn(value);
    validateDate(startOn, value);
  };

  const onAccessSettingChange = (e) => {
    setAccessSetting(e.currentTarget.value);
    if (e.currentTarget.value === "anyone_as_guest") {
      setAnonymitySetting("anonymous");
    }
  };

  const onAnonymitySettingChange = (e) => {
    setAnonymitySetting(e.currentTarget.value);
  };

  const onAddQuestion = () => {
    setQuestions([...questions, defaultQuestion(productType)]);
    // onQuestionSetFocus(questions.length);
  };

  const onInsertQuestion = (index) => {
    const temp = [...questions];
    temp.splice(index, 0, defaultQuestion(productType));
    setQuestions(temp);
  };

  const onRemoveQuestion = (index) => {
    const temp = [...questions];
    temp.splice(index, 1);
    setQuestions(temp);
  };

  const onMoveQuestion = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    setQuestions(
      reorder(questions, result.source.index, result.destination.index)
    );
  };

  const onQuestionFieldChange = (name, index) => (event) => {
    let newArr = questions.map((item, i) => {
      if (index === i) {
        return { ...item, [name]: event.target.value };
      } else {
        return item;
      }
    });
    setQuestions(newArr);
  };

  const onSelectTemplateQuestion = (question, question_type, index) => {
    console.log("Add template question", question, question_type);
    // setQuestions([...questions, templateQuestion(question, question_type)]);
    let newArr = questions.map((item, i) => {
      if (index === i) {
        return templateQuestion(question, question_type);
      } else {
        return item;
      }
    });
    setQuestions(newArr);
  };

  const onQuestionSetFocus = (index) => () => {
    let newArr = questions.map((item, i) => {
      return { ...item, hasFocus: index === i };
    });
    setQuestions(newArr);
  };

  const onQuestionMediaChange = (questionIndex, media) => {
    const updatedQuestions = produce(questions, (draft) => {
      if (!media) {
        draft[questionIndex].media = {};
      } else {
        draft[questionIndex].media = media;
      }
    });
    setQuestions(updatedQuestions);
  };

  const onAddOption = (questionIndex) => {
    const updatedQuestions = produce(questions, (draft) => {
      draft[questionIndex].options.push({
        name: `Option ${draft[questionIndex].options.length + 1}`,
      });
    });
    setQuestions(updatedQuestions);
  };

  const onRemoveOption = (questionIndex, optionIndex) => {
    const updatedQuestions = produce(questions, (draft) => {
      draft[questionIndex].options.splice(optionIndex, 1);
    });
    setQuestions(updatedQuestions);
  };

  const onOptionTextChange = (questionIndex, optionIndex, newValue) => {
    const updatedQuestions = produce(questions, (draft) => {
      draft[questionIndex].options[optionIndex].name = newValue;
    });
    setQuestions(updatedQuestions);
  };

  const onOptionMediaChange = (questionIndex, optionIndex, media) => {
    const updatedQuestions = produce(questions, (draft) => {
      draft[questionIndex].options[optionIndex].media = media;
    });
    setQuestions(updatedQuestions);
  };

  const onQuestionTypeChange = (index) => (event) => {
    const newQuestionType = event.target.value;
    let newArr = questions.map((item, i) => {
      if (index === i) {
        return {
          ...item,
          question_type: newQuestionType,
          options: QUESTION_TYPES[newQuestionType].options,
        };
      } else {
        return item;
      }
    });
    setQuestions(newArr);
  };

  const handleCancel = () => {
    if (history.length > 0) {
      history.goBack();
    } else {
      history.push("/");
    }
  };

  const validate = () => {
    if (errors.duration) return false;
    for (const q of questions) {
      if (!q.title) return false;
    }
    return true;
  };

  const handleSubmit = () => {
    const save = async () => {
      try {
        setSubmitting(true);
        const updatedEventIDs = [];
        const publishedEvents = [];

        for (const q of questions) {
          // console.log("Saving Question", q);

          let event = {
            event_id: q.id,
            name: q.title,
            schedule: {
              ideas: {
                start_time: DateTime.momentToUnixSeconds(startOn),
                end_time: DateTime.momentToUnixSeconds(endOn),
              },
              voting: {
                start_time: DateTime.momentToUnixSeconds(startOn),
                end_time: DateTime.momentToUnixSeconds(endOn),
              },
            },
            time_zone: moment.tz.guess(),
            question_type: q.question_type,
            visibility_setting: accessSetting,
            anonymous: anonymitySetting !== "named",
          };

          if (q.media && q.media.id) {
            event.launch_media_id = q.media.id;
            event.launch_media = q.media;
          } else {
            event.launch_media_id = null;
            event.launch_media = null;
          }

          if (
            q.question_type === "multiple" ||
            q.question_type === "multiple_rating" ||
            q.question_type === "stack_rank"
          ) {
            event.multiple_choices = q.options.map((o) => o.name);
            event.randomize_choice_order = q.randomize_choice_order;
            event.polls_crowdsourcing = q.enable_comments;
          } else if (
            q.question_type === "binary" ||
            q.question_type === "scale"
          ) {
            event.polls_crowdsourcing = q.enable_comments;
          } else if (q.question_type === "default") {
            event.voting_mode = q.voting_mode;
          }

          // console.log("Saving Event", event);

          const updatedEvent = event.event_id
            ? await eventApi.update(event)
            : await eventApi.create(event);

          updatedEventIDs.push(updatedEvent.id);

          // handle media attachments for question with multiple options
          if (q.options && q.options.find((o) => o.media)) {
            const ideas = await ideaApi.getIdeas(updatedEvent.id);
            for (let i = 0; i < ideas.length; i++) {
              let idea = ideas[i];
              if (
                q.options[i].media &&
                q.options[i].media.id !== idea.media_id
              ) {
                idea.media_id = q.options[i].media.id;
                await ideaApi.updateIdea(idea);
              }
            }
          }
        }

        if (collectionID) {
          await collectionApi.update({
            id: collectionID,
            name: title,
            event_ids: updatedEventIDs,
          });
          // console.log("Collection updated", updatedCollection);
        } else {
          const createdCollection = await collectionApi.create({
            name: title,
            type: productType === "survey" ? "survey" : "concurrent",
            event_ids: updatedEventIDs,
          });

          // console.log("Collection created", createdCollection);
          TrackingAnalytics.send(`POPin Created`, {
            product: createdCollection.type,
            question_count: updatedEventIDs.length,
          });
        }

        for (const updatedEventID of updatedEventIDs) {
          const publishedEvent = await eventApi.publish(updatedEventID);
          publishedEvents.push(publishedEvent.slug);
        }

        NavigationUtils.routeToLegacy(
          `/q/${updatedEventIDs[0]}/answers?created_now=1`
        );
      } catch (err) {
        console.log("Error!!", err);
      } finally {
        setSubmitting(false);
      }
    };

    save();
  };

  if (loading) {
    return (
      <div className="col h-100 w-100 d-flex">
        <div className="m-auto col">
          <LoadingAndPulse size={20} color="#3D9AE2" />
        </div>
      </div>
    );
  }

  if (BrowserChecker.isIe()) {
    return <BrowserNotSupported currentSlug={currentSlug} />;
  }

  return (
    <div className="container-wrapper">
      <div className="prioritize container create-page">
        <NameDialog
          isOpen={isNameDialogOpen}
          onSaveName={() => setNameDialogOpen(false)}
        />
        <h1>{productType === "survey" ? "Survey" : "Crowdsource"}</h1>
        {/* Needed for debugging sometimes
        {true && (
          <div style={{ fontSize: "10px" }}>
            {title} | {endOn.calendar()} | {accessSetting} | {anonymitySetting}{" "}
            | {collection.id} | {collection.short_code}
            <br />
            <ul>
              {questions.map((q) => (
                <li key={q.key}>
                  {q.title} - {q.question_type} - {q.media?.url}
                  {q.options && (
                    <ul>
                      {q.options.map((o) => (
                        <li>{o.name}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )} */}
        <div className="title-date section">
          <LabeledContainer
            titleWarning={false}
            className="create-step1__input_title"
            title={"Add a Title for your POPin:"}
          >
            <Input
              name="title"
              placeholder={"Example: "}
              onChange={onTitleChange}
              onFocus={(e) => e.target.select()}
              value={title}
              required={true}
              maxLength="40"
              autoFocus
            />
          </LabeledContainer>

          <div>
            <label>Set the Duration (aka the Run Time):</label>
            <div className="date-fields">
              <DatePicker
                size="large"
                name="startOn"
                format="ddd MMM D[,] h:mm a zz"
                allowClear={false}
                showToday={false}
                value={moment(startOn)}
                showTime={{
                  use12Hours: true,
                  format: "h:mm a",
                  defaultValue: moment(startOn),
                  minuteStep: 15,
                }}
                onChange={onStartOnChange}
              />
              <span>to</span>
              <DatePicker
                size="large"
                name="endOn"
                format="ddd MMM D[,] h:mm a zz"
                value={moment(endOn)}
                allowClear={false}
                showToday={false}
                showTime={{
                  use12Hours: true,
                  format: "h:mm a",
                  defaultValue: moment(endOn),
                  minuteStep: 15,
                }}
                onChange={onEndOnChange}
              />
            </div>
            {errors.duration && <div className="error">{errors.duration}</div>}
          </div>
        </div>
        <div className="question-editor section">
          <LabeledContainer title={"Create your Question(s):"} />
                    <QuestionEditor
            productType={productType}
            questions={questions}
            surveyObjective={title}
            onQuestionSetFocus={onQuestionSetFocus}
            onQuestionFieldChange={onQuestionFieldChange}
            onQuestionMediaChange={onQuestionMediaChange}
            onQuestionTypeChange={onQuestionTypeChange}
            onAddQuestion={onAddQuestion}
            onSelectTemplateQuestion={onSelectTemplateQuestion}
            onInsertQuestion={onInsertQuestion}
            onRemoveQuestion={onRemoveQuestion}
            onMoveQuestion={onMoveQuestion}
            onAddOption={onAddOption}
            onRemoveOption={onRemoveOption}
            onOptionTextChange={onOptionTextChange}
            onOptionMediaChange={onOptionMediaChange}
          />
        </div>
        <div className="participation-setting section">
          <LabeledContainer title={"Establish your Participation Settings:"}>
            <ParticipationSetting
              accessSetting={accessSetting}
              anonymitySetting={anonymitySetting}
              isAnonymitySettingEditable={isAnonymitySettingEditable}
              onAccessSettingChange={onAccessSettingChange}
              onAnonymitySettingChange={onAnonymitySettingChange}
            />
          </LabeledContainer>
        </div>
        <div className="footer section">
          <Button lg flat cancel onClick={handleCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            lg
            primary
            submit
            onClick={handleSubmit}
            disabled={!validate() || submitting}
          >
            {submitting ? "Saving ..." : ( accessSetting === "invite_only" ? "Next" : "Save & Preview" )}
          </Button>
        </div>
      </div>
    </div>
  );
};

const BrowserNotSupported = ({ currentSlug }) => {
  const history = useHistory();
  const goToDashboard = () => history.push("/r/dashboard");
  // const goToOldCreate = () => {
  //   if (currentSlug) {
  //     NavigationUtils.routeToLegacy(`/q/${currentSlug}/edit`);
  //   } else {
  //     NavigationUtils.routeToLegacy("/q/edit");
  //   }
  // };
  return (
    <DialogModalWindow
      isOpen={true}
      cancelBtn={false}
      confirmBtn={false}
      onCancel={goToDashboard}
    >
      <h1>Please Upgrade Your Browser</h1>
      <p>
        Internet Explorer 11 (or older) is no longer a supported browser and
        will not work with many of POPin's features.
      </p>
      <p>
        In order to create a POPin, please switch to a recommended browser like{" "}
        <a target="_blank" href="https://www.microsoft.com/en-us/edge" rel="noopener noreferrer">
          Microsoft Edge
        </a>{" "}
        or{" "}
        <a target="_blank" href="https://www.google.com/chrome/" rel="noopener noreferrer">
          Google Chrome
        </a>
        .
      </p>
      <p>&nbsp;</p>
      <button onClick={goToDashboard} className="btn-submit">
        Back to Dashboard
      </button>
    </DialogModalWindow>
  );
};

export default Create;
